import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Undo2, Loader2, Link2 } from "lucide-react";
import { api } from "@/lib/api";
import { useProjectCtx } from "@/context/ProjectContext";
import { useCompany } from "@/context/CompanyContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { fmtDate, fmtNum, todayISO } from "@/lib/format";
import { getErrorMessage } from "@/lib/errors";
import { CompanyField } from "@/components/CompanyField";

const COST_CATEGORIES = ["SUBCONTRACT", "MATERIAL", "LABOUR", "OTHER"];

export default function CostEntries() {
  const { projectId, isProjectManager } = useProjectCtx();
  const { companies } = useCompany() || {};
  const [entries, setEntries] = useState([]);
  const [wbsItems, setWbsItems] = useState([]);
  const [filters, setFilters] = useState({ category: "__all__", date_from: "", date_to: "" });
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [assigning, setAssigning] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.category !== "__all__") params.category = filters.category;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      const [{ data: rows }, { data: wbs }] = await Promise.all([
        api.get(`/projects/${projectId}/cost-entries`, { params }),
        api.get(`/projects/${projectId}/wbs`, { params: { include_groups: false } }),
      ]);
      setEntries(rows);
      setWbsItems(wbs);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [projectId, filters]);

  useEffect(() => { load(); }, [load]);

  const itemLabel = (id) => {
    const item = wbsItems.find((w) => w.id === id);
    return item ? `${item.code} — ${item.name}` : "—";
  };

  const activityCell = (entry) => {
    if (entry.wbs_item_id) return itemLabel(entry.wbs_item_id);
    const traced = (entry.traced_wbs_item_ids || []).map(itemLabel);
    if (traced.length) return <span title="Taken from the PO / Work Order line">{traced.join(", ")} <span className="text-[10px] opacity-70">(via PO / WO)</span></span>;
    return "—";
  };

  const reverse = async (entry) => {
    setBusyId(entry.id);
    try {
      await api.post(`/projects/${projectId}/cost-entries/${entry.id}/reverse`);
      toast.success("Entry reversed");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="cost-entries-page">
      <PageHeader
        title="Cost Entries"
        description="Actual project costs — manually booked here, or raised automatically from Service Bills and Goods Issues."
        action={
          <div className="flex items-center gap-2">
            <Select value={filters.category} onValueChange={(v) => setFilters({ ...filters, category: v })}>
              <SelectTrigger className="w-36" data-testid="cost-entries-category-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All categories</SelectItem>
                {COST_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} data-testid="cost-entries-date-from-input" className="w-36" />
            <Input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} data-testid="cost-entries-date-to-input" className="w-36" />
            {isProjectManager && <CreateCostEntryDialog projectId={projectId} companies={companies} wbsItems={wbsItems} onCreated={load} />}
          </div>
        }
      />

      <div className="rounded-xl border border-border bg-card">
        {!loading && entries.length === 0 ? (
          <EmptyState title="No cost entries yet" testId="cost-entries-empty-state" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow><TableHead>Date</TableHead><TableHead>Activity</TableHead><TableHead>Category</TableHead><TableHead>Description</TableHead><TableHead className="text-right">Amount</TableHead><TableHead>Source</TableHead><TableHead></TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {entries.map((entry) => (
                <TableRow key={entry.id} data-testid={`cost-entry-row-${entry.id}`}>
                  <TableCell className="font-mono text-xs text-muted-foreground">{fmtDate(entry.entry_date)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{activityCell(entry)}</TableCell>
                  <TableCell className="text-xs text-foreground">{entry.category}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{entry.description || "—"}</TableCell>
                  <TableCell className={`text-right font-mono text-xs font-semibold ${Number(entry.amount) < 0 ? "text-rose-400" : "text-foreground"}`}>{fmtNum(entry.amount, 2)}</TableCell>
                  <TableCell>
                    <Badge variant={entry.source_type === "MANUAL" ? "secondary" : "outline"} data-testid={`cost-entry-source-badge-${entry.id}`}>{entry.source_type}{entry.is_reversed ? " · reversed" : ""}</Badge>
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    {isProjectManager && !entry.wbs_item_id && !(entry.traced_wbs_item_ids || []).length && (
                      <Button size="sm" variant="ghost" onClick={() => setAssigning(entry)} title="Assign to an activity" data-testid={`assign-cost-entry-button-${entry.id}`}>
                        <Link2 className="h-3.5 w-3.5" /> <span className="ml-1 text-xs">Assign</span>
                      </Button>
                    )}
                    {isProjectManager && entry.source_type === "MANUAL" && !entry.is_reversed && Number(entry.amount) > 0 && (
                      <Button size="sm" variant="ghost" disabled={busyId === entry.id} onClick={() => reverse(entry)} data-testid={`reverse-cost-entry-button-${entry.id}`}>
                        <Undo2 className="h-3.5 w-3.5 text-rose-400" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
      <AssignActivityDialog entry={assigning} projectId={projectId} wbsItems={wbsItems} onClose={() => setAssigning(null)} onDone={() => { setAssigning(null); load(); }} />
    </div>
  );
}

function AssignActivityDialog({ entry, projectId, wbsItems, onClose, onDone }) {
  const [wbs, setWbs] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setWbs(""); }, [entry]);
  const save = async () => {
    if (!wbs) return;
    setSaving(true);
    try {
      await api.post(`/projects/${projectId}/cost-entries/${entry.id}/activity`, { wbs_item_id: wbs });
      toast.success("Cost assigned to the activity");
      onDone();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={!!entry} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Assign to an activity</DialogTitle></DialogHeader>
        {entry && (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">{entry.description || entry.category} — <span className="font-mono">{fmtNum(entry.amount, 2)}</span></p>
            <div>
              <Label>Activity</Label>
              <Select value={wbs} onValueChange={setWbs}>
                <SelectTrigger className="mt-1.5" data-testid="assign-activity-select"><SelectValue placeholder="Select an activity" /></SelectTrigger>
                <SelectContent>{wbsItems.map((w) => <SelectItem key={w.id} value={w.id}>{w.code} — {w.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <p className="text-[11px] text-muted-foreground">The bill or expense itself is not changed — only which activity this cost counts against in Budget vs Actual.</p>
          </div>
        )}
        <DialogFooter>
          <Button disabled={!wbs || saving} onClick={save} data-testid="assign-activity-save">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Assign</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreateCostEntryDialog({ projectId, companies, wbsItems, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ company_id: "", wbs_item_id: "", category: "SUBCONTRACT", entry_date: todayISO(), description: "", amount: "" });
  const [saving, setSaving] = useState(false);
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/projects/${projectId}/cost-entries`, { ...form, wbs_item_id: form.wbs_item_id || null });
      toast.success("Cost entry booked");
      setForm({ company_id: "", wbs_item_id: "", category: "SUBCONTRACT", entry_date: todayISO(), description: "", amount: "" });
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" data-testid="create-cost-entry-button"><Plus className="h-3.5 w-3.5" /> Book cost</Button></DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader><DialogTitle>Manual Cost Entry</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="create-cost-entry-form">
          <div className="grid grid-cols-2 gap-3">
            <CompanyField label="Company" value={form.company_id} onChange={(v) => setForm({ ...form, company_id: v })} testId="cost-entry-company-select" />
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="mt-1.5" data-testid="cost-entry-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{COST_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Activity (optional)</Label>
            <Select value={form.wbs_item_id || "__none__"} onValueChange={(v) => setForm({ ...form, wbs_item_id: v === "__none__" ? "" : v })}>
              <SelectTrigger className="mt-1.5" data-testid="cost-entry-activity-select"><SelectValue placeholder="Not linked" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Not linked</SelectItem>
                {wbsItems.map((w) => <SelectItem key={w.id} value={w.id}>{w.code} — {w.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Date</Label><Input required type="date" value={form.entry_date} onChange={set("entry_date")} className="mt-1.5" data-testid="cost-entry-date-input" /></div>
            <div><Label>Amount</Label><Input required type="number" step="0.01" value={form.amount} onChange={set("amount")} className="mt-1.5" data-testid="cost-entry-amount-input" /></div>
          </div>
          <div><Label>Description</Label><Input required value={form.description} onChange={set("description")} className="mt-1.5" data-testid="cost-entry-description-input" /></div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="create-cost-entry-submit-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
